import { Navigate, useNavigate, useParams } from 'react-router';
import { TabPills } from '@skupreme/shared/ui';
import { ORG_TABS, useOrgTab } from '../api/orgs';
import type { OrgTab } from '../api/types';
import { ConnectionsTab } from './org/ConnectionsTab';
import { HoldsTab } from './org/HoldsTab';
import { MembersTab } from './org/MembersTab';
import { OverviewTab } from './org/OverviewTab';
import { StockSyncTab } from './org/StockSyncTab';

const TABS: { id: OrgTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'members', label: 'Members' },
  { id: 'connections', label: 'Connections' },
  { id: 'holds', label: 'Holds' },
  { id: 'stock-sync', label: 'Stock sync' },
];

const isOrgTab = (value: string): value is OrgTab =>
  (ORG_TABS as string[]).includes(value);

export function OrgPage() {
  const { id = '', tab = 'overview' } = useParams();
  const navigate = useNavigate();
  // Same query as the Overview tab, so it is one GET; the heading just wants the name.
  const overview = useOrgTab(id, 'overview');

  if (!isOrgTab(tab)) {
    return <Navigate to={`/orgs/${encodeURIComponent(id)}`} replace />;
  }
  const open = (next: string) =>
    navigate(`/orgs/${encodeURIComponent(id)}/${next}`);

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      <div>
        <h1 className="sk:text-lg sk:font-semibold">
          {overview.data?.account.name ?? id}
        </h1>
        <p className="sk:text-dense sk:text-fg-tertiary">{id}</p>
      </div>
      <TabPills
        label="Organization sections"
        items={TABS}
        selectedKey={tab}
        onSelectionChange={open}
      />
      {tab === 'overview' && <OverviewTab orgId={id} />}
      {tab === 'members' && <MembersTab orgId={id} />}
      {tab === 'connections' && <ConnectionsTab orgId={id} />}
      {tab === 'holds' && <HoldsTab orgId={id} />}
      {tab === 'stock-sync' && <StockSyncTab orgId={id} />}
    </div>
  );
}
